import FormZeroAjax from '../../../../../../src/packages/form-zero/resources/js/FormZeroAjax.mjs';
import ArticleComponentFeedback from './ArticleComponentFeedback.js';
import ArticleDeleteConfirmation from './ArticleDeleteConfirmation.js';

/**
 * Article-only UI hooks on top of FormZero's reusable fragment transport.
 * Validation, scoped serialization, tokens, response handling and DOM insertion
 * live in FormZeroAjax; this adapter knows only about the Article editor UI.
 */
export default class ArticleEditorFragments {
    constructor(bootstrap, articleEditDrag, translator, toastNotifier = null, confirmationDialog = null) {
        this.bootstrap = bootstrap;
        this.articleEditDrag = articleEditDrag;
        this.toastNotifier = toastNotifier;
        this.mainSavePending = false;
        this.componentFeedback = new ArticleComponentFeedback((key) => translator.trans(key));
        this.deleteConfirmation = confirmationDialog || new ArticleDeleteConfirmation((key) => translator.trans(key));
        this.ajax = new FormZeroAjax({
            translate: (key) => translator.trans(key),
            invalidResponse: 'article.sectionInvalidResponse',
            requestError: 'article.sectionRequestError',
            dirtyMessage: 'article.unsavedChanges',
            savedMessage: 'article.fragmentSaved',
            // Toasts for page-level operations, component-local feedback for
            // independently saved rows. Errors also reach the toast because a
            // failed save may be in a collapsed accordion.
            notify: toastNotifier ? ({type, message}) => toastNotifier.show({type, message}) : null,
            confirm: (message, trigger) => this.deleteConfirmation.ask(message, trigger),
            onRowStatus: (state) => this.componentFeedback.update(state),
            inlineDirty: false,
            statusClasses: {base: 'ms-2 small', success: 'text-success', error: 'text-danger', warning: 'text-warning'},
        });
    }

    init(root = document) {
        const form = root.querySelector('form[name="ArticleForm"][data-fz-token]');
        if (!form || form.dataset.articleFragmentsInitialized === 'true') return;
        form.dataset.articleFragmentsInitialized = 'true';
        this.ajax.init(form);
        this.articleEditDrag.setPersist?.((editor, type, ownerId, ids) =>
            this.ajax.reorder(editor, editor.dataset.fzReorderUrl, type, ownerId, ids));

        // With JavaScript, the primary Save button updates ONLY the article
        // fields. The legacy full-tree POST is retained as a no-JS fallback.
        // Never submit nested snapshots after an independent AJAX mutation.
        form.addEventListener?.('submit', (event) => {
            event.preventDefault();
            void this.saveMain(form, event.submitter);
        });
        document.addEventListener('formzero:committed', (event) => {
            if (event.detail.form === form) form.dataset.articleFullSaveStale = 'true';
        });
        document.addEventListener('formzero:fragment-saved', (event) => {
            if (event.detail.form !== form) return;
            form.dataset.articleFullSaveStale = 'true';
        });
        document.addEventListener('formzero:reordered', (event) => {
            if (event.detail.form === form) form.dataset.articleFullSaveStale = 'true';
        });
        document.addEventListener('formzero:fragment-removed', (event) => {
            if (event.detail.form === form) form.dataset.articleFullSaveStale = 'true';
        });
        document.addEventListener('formzero:conflict', (event) => {
            if (event.detail.form === form) form.dataset.articleFullSaveStale = 'true';
        });
        // Physical deletion happens after the successful database commit.
        // If unlink failed, the record is gone but an orphan needs cleanup.
        document.addEventListener('formzero:fragment-warning', (event) => {
            if (event.detail.form !== form) return;
            const message = event.detail.result.warning;
            if (this.toastNotifier) this.toastNotifier.warning(this.ajax.translate(message));
            else this.mainSaveNotice(form, message, true);
        });

        document.addEventListener('formzero:before-submit', (event) => {
            if (event.detail.form !== form) return;
            if (/^content-\d+$/.test(event.detail.root.id)) {
                window.VimarQuillEditor?.sync(event.detail.root);
            }
        });

        const added = (event) => {
            if (event.detail.form !== form) return;
            const row = event.detail.root;
            this.articleEditDrag.init(row);
            const isNew = ['create', 'upload'].includes(event.detail.trigger.dataset.fzAction);
            this.openRow(row, isNew);
            if (isNew && event.detail.trigger.dataset.fzAction === 'upload') {
                row.scrollIntoView?.({block: 'center'});
            }
            if (/^content-\d+$/.test(row.id)) {
                // Preserve the existing Quill integration and its idempotent init.
                document.dispatchEvent(new CustomEvent('article:content-added', {detail: {root: row}}));
            }
        };
        document.addEventListener('formzero:fragment-added', added);
        document.addEventListener('formzero:fragment-replaced', added);
    }

    /**
     * Collect the article's own fields and its metadata/date group only.
     * A browser form POST contains the whole article tree; reusing it here
     * could overwrite saved children or submit unrelated large file inputs.
     */
    static collectMain(form) {
        const rootFields = new Set([
            'title', 'description', 'options', 'brands', 'areas', 'treeId',
            'parentId', 'lang', 'profileAudience', 'profileRoles', 'notes',
            'wkf', 'imageId', 'active', 'indexed',
        ]);
        const nestedFields = {
            metadata: new Set(['metaTitle', 'metaDescription', 'pageType', 'smartDeskType']),
            dates: new Set(['rankAt', 'startedAt', 'endedAt', 'publishedAt', 'unpublishedAt']),
        };
        const payload = new FormData();
        for (const control of form.querySelectorAll('input[name],select[name],textarea[name]')) {
            const name = control.name || '';
            if (!name || control.disabled || ['file', 'submit', 'button', 'reset'].includes(control.type)) continue;
            const directName = name.replace(/\[\]$/, '');
            const nested = /^basedata\[(metadata|dates)\]\[([a-zA-Z][a-zA-Z0-9]*)\](?:\[\])?$/.exec(name);
            if (!(rootFields.has(directName) && (name === directName || name === `${directName}[]`)) &&
                !(nested && nestedFields[nested[1]].has(nested[2]))) continue;
            if (['checkbox', 'radio'].includes(control.type) && !control.checked) continue;
            if (control.tagName === 'SELECT' && control.multiple) {
                for (const option of control.selectedOptions) payload.append(name, option.value);
            } else {
                payload.append(name, control.value);
            }
        }
        payload.set('article_main_fragment', '1');
        return payload;
    }

    mainSaveNotice(form, message, isError = false) {
        const translated = this.ajax.translate(message);
        if (this.toastNotifier) {
            if (isError) this.toastNotifier.error(translated);
            else this.toastNotifier.success(translated);
            return;
        }
        let notice = form.querySelector('#article-main-save-notice');
        if (!notice) {
            notice = document.createElement('div');
            notice.id = 'article-main-save-notice';
            form.prepend(notice);
        }
        notice.className = `alert ${isError ? 'alert-danger' : 'alert-success'}`;
        notice.setAttribute('role', isError ? 'alert' : 'status');
        notice.setAttribute('aria-live', isError ? 'assertive' : 'polite');
        notice.textContent = translated;
        notice.scrollIntoView?.({block: 'nearest'});
    }

    async saveMain(form, submitter = null) {
        if (this.mainSavePending) return;
        this.mainSavePending = true;
        const wasDisabled = submitter?.disabled;
        if (submitter && 'disabled' in submitter) submitter.disabled = true;
        try {
            const payload = ArticleEditorFragments.collectMain(form);
            const result = await this.ajax.enqueue(form, () => this.ajax.request(form, form.action, payload));
            this.ajax.emit('committed', {form, trigger: submitter, result, action: 'save-main'});
            const hasDirtyRows = [...this.ajax.dirtyRows].some((row) => row.isConnected !== false && form.contains(row));
            this.mainSaveNotice(form, hasDirtyRows
                ? 'Dati articolo salvati. Le modifiche ai riquadri non ancora salvate devono essere salvate separatamente.'
                : 'Dati articolo salvati correttamente.');
        } catch (error) {
            this.mainSaveNotice(form, error.message || 'Errore durante il salvataggio', true);
            this.ajax.emit('fragment-error', {form, trigger: submitter, error});
            if (error.conflict) this.ajax.emit('conflict', {form, trigger: submitter, error});
        } finally {
            if (submitter && 'disabled' in submitter) submitter.disabled = wasDisabled;
            this.mainSavePending = false;
        }
    }

    openRow(row, focus = false) {
        const collapse = row.querySelector('.accordion-collapse');
        if (collapse) {
            this.bootstrap.Collapse.getOrCreateInstance(collapse, {toggle: false}).show();
        }
        if (!focus) return;
        const field = /^partner-/.test(row.id) ? 'targetId'
            : /^link-/.test(row.id) ? 'text' : 'title';
        row.querySelector(`input[name$="[${field}]"]`)?.focus();
    }
}
